import {
  DirectionsCar as DirectionsCarIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { InventoryLead, InventoryLeadTruck } from '@otw/models/leads/inventoryLead';
import React from 'react';
import { useInventoryAssignableUsers } from 'hooks/useUsers';
import { resolveInventoryRepName } from '../../util/inventoryRepDisplay';
import type { StatusOption } from './LeadInfoCard';

export interface InventoryLeadInfoCardProps {
  lead: InventoryLead;
  contactName: string;
  companyName: string;
  columnLabel: string;
  columnColor: string;
  statusOptions: StatusOption[];
  onClick?: (lead: InventoryLead) => void;
  onEdit?: (lead: InventoryLead) => void;
  onStatusChange?: (
    lead: InventoryLead,
    newStatus: string,
    newStatusLabel: string,
  ) => void;
  isUpdatingStatus?: boolean;
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const formatDate = (date: Date | null | undefined): string => {
  if (!date) return '—';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  return dateFormatter.format(d);
};

const formatTruckLine = (truck: InventoryLeadTruck): string => {
  const main = [truck.year || '', truck.make, truck.model].filter(Boolean).join(' ').trim();
  const meta = [truck.type, truck.size, truck.fuel].filter(Boolean).join(' · ');
  const asking =
    typeof truck.askingPrice === 'number' && Number.isFinite(truck.askingPrice)
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(truck.askingPrice)
      : '';
  const vin = truck.vin?.trim();
  return [main || 'Truck', meta, asking, vin ? `VIN ${vin}` : '']
    .filter(Boolean)
    .join(' · ');
};

const InventoryLeadInfoCard: React.FC<InventoryLeadInfoCardProps> = ({
  lead,
  contactName,
  companyName,
  columnLabel,
  columnColor,
  statusOptions,
  onClick,
  onEdit,
  onStatusChange,
  isUpdatingStatus = false,
}) => {
  const theme = useTheme();
  const { users: inventoryReps } = useInventoryAssignableUsers();
  const statusValue = lead.status ?? '';
  const truckCount = lead.trucks?.length ?? 0;
  const trucks = lead.trucks ?? [];
  const statusSelectAccent = columnColor;
  const title = companyName?.trim() || contactName?.trim() || lead.leadNumber || 'Inventory lead';
  const isInteractive = Boolean(onClick);

  return (
    <Paper
      variant="outlined"
      onClick={onClick ? () => onClick(lead) : undefined}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={
        isInteractive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.(lead);
              }
            }
          : undefined
      }
      sx={{
        p: 2,
        borderRadius: 2,
        borderLeft: `4px solid ${columnColor}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        cursor: isInteractive ? 'pointer' : 'default',
        transition: 'box-shadow 150ms ease, border-color 150ms ease',
        '&:hover': isInteractive
          ? {
              boxShadow: `0 6px 18px ${alpha(columnColor, 0.18)}`,
              borderColor: alpha(columnColor, 0.55),
            }
          : undefined,
        '&:focus-visible': isInteractive
          ? { outline: `2px solid ${alpha(columnColor, 0.6)}`, outlineOffset: 2 }
          : undefined,
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle2"
            fontWeight={800}
            sx={{ lineHeight: 1.2 }}
            noWrap
            title={title}
          >
            {title}
          </Typography>
          {companyName?.trim() && contactName?.trim() ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block' }}
              noWrap
            >
              {contactName}
            </Typography>
          ) : null}
        </Box>
        <Stack
          direction="row"
          alignItems="flex-start"
          spacing={0.5}
          sx={{ flexShrink: 0 }}
        >
          {lead.leadNumber?.trim() ? (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: columnColor,
                letterSpacing: 0.4,
                whiteSpace: 'nowrap',
                pt: 0.5,
              }}
              title={`Lead # ${lead.leadNumber.trim()}`}
            >
              {lead.leadNumber.trim()}
            </Typography>
          ) : null}
          {onEdit ? (
            <Tooltip title="Edit inventory lead">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(lead);
                }}
                aria-label="Edit inventory lead"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>

      {onStatusChange ? (
        <TextField
          select
          size="small"
          label="Status"
          value={statusValue}
          disabled={isUpdatingStatus || statusOptions.length === 0}
          onChange={(e) => {
            const next = e.target.value;
            if (next === statusValue) return;
            const opt = statusOptions.find((o) => o.id === next);
            onStatusChange(lead, next, opt?.label ?? next);
          }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          SelectProps={{
            MenuProps: {
              onClick: (e) => e.stopPropagation(),
            },
            renderValue: (value) => {
              const opt = statusOptions.find((o) => o.id === value);
              const label = opt?.label ?? columnLabel;
              const color = opt?.color ?? statusSelectAccent;
              return (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {label}
                  </Typography>
                </Stack>
              );
            },
          }}
          InputProps={{
            sx: {
              borderRadius: 2,
              bgcolor: alpha(statusSelectAccent, 0.06),
            },
          }}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(statusSelectAccent, 0.4),
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(statusSelectAccent, 0.7),
            },
          }}
        >
          {statusOptions.map((opt) => (
            <MenuItem key={opt.id} value={opt.id} sx={{ fontSize: 14 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: opt.color,
                  }}
                />
                <Typography variant="body2" fontWeight={600}>
                  {opt.label}
                </Typography>
              </Stack>
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <Chip
          size="small"
          label={columnLabel}
          variant="outlined"
          sx={{
            alignSelf: 'flex-start',
            height: 22,
            fontWeight: 700,
            borderLeft: `3px solid ${columnColor}`,
            bgcolor: alpha(columnColor, 0.08),
            borderColor: alpha(columnColor, 0.35),
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      )}

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
        <Chip
          size="small"
          icon={<DirectionsCarIcon sx={{ fontSize: 14 }} />}
          label={`${truckCount} truck${truckCount === 1 ? '' : 's'}`}
          sx={{
            height: 20,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: alpha(theme.palette.info.main, 0.12),
            color: theme.palette.info.dark,
            '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        <Chip
          size="small"
          icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
          label="Inventory"
          sx={{
            height: 20,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: alpha('#00897b', 0.12),
            color: '#00695c',
            '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      </Stack>

      {trucks.length > 0 ? (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.1, mb: 0.5 }}
          >
            Trucks
          </Typography>
          <Stack spacing={0.35}>
            {trucks.map((truck, index) => (
              <Typography
                key={`${truck.vin || truck.make}-${index}`}
                variant="body2"
                fontWeight={600}
                sx={{ wordBreak: 'break-word' }}
              >
                {formatTruckLine(truck)}
              </Typography>
            ))}
          </Stack>
        </Box>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          rowGap: 1,
          columnGap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.1 }}
          >
            Assigned rep
          </Typography>
          <Typography variant="body2" fontWeight={600} noWrap>
            {resolveInventoryRepName(inventoryReps, lead.assignedRepId)}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.1 }}
          >
            Updated
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatDate(lead.updatedAt)}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.1 }}
          >
            Created
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatDate(lead.createdAt)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default InventoryLeadInfoCard;
